
import { WrapResponse } from '@/types';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import toast from 'react-hot-toast';
import { api, host } from '@/lib/env';
import { fun } from '@/lib/request';
import * as Icons from "@/components/fun-ui/icons";
import { SearchHistoryItem } from '@/components/paper-search/types';

export type PaymentType = 'upgrade' | 'upgradePlus3x' | 'recharge';

/** 创建会员订单时传给后端的 vip_type */
export type VipTypeOrder = 'plus' | 'plus_3x';
export type GlobalConfig = {
  // 论文最大上传数量
  maxSummaryCount: number;
  // Plus会员，论文最大上传数量，默认100篇
  maxVipSummaryCount: number;
  pdfMaxSize: number;
  contactImageUrl: string;
  paymentTabs: {
    value: PaymentType;
    label: string;
  }[];
  pc: {
    // 官网首页桌面端下载入口
    desktopDownload?: {
      enabled?: boolean;
      title?: string;
      href?: string;
    };
    // PC中监测移动端访问
    mobileDetectImageUrls: string[];
    // 营销
    marketing: {
      starting: Boolean;
      entranceImageUrl: string;
      entranceFoldImageUrl: string;
      marketingImageUrl: string;
      marketingImage2Url: string;
      marketingSurveyUrl: string;
      // v2 图片数组 2501版本
      marketingImagesV2501: string[] | {
        html: string;
        image: string;
      }[];
      marketingBackgroundColor: string;
      // 定向人群展示：'all' 全部会员（默认），'normal' 仅普通会员，'vip' 仅VIP
      targetAudience?: 'all' | 'normal' | 'vip';
    },
    //产品站
    literatureReview: {
      title: string;
      subtitle: string;
      url: string;
      features: {
        title: string;
        option?: string[];
        caption: string;
        url: string;
      }[];
    },
    //产品站
    literatureTable: {
      title: string;
      subtitle: string;
      backgroundImage: string;
      features: {
        title: string;
        option?: string[];
        caption: string;
        url: string;
      }[];
    }
  },
  traceOriginAutoSendMessageTemplate: string;
};

export type AssistantsInputSchema = {
  key: string;
  label: string;
  value?: string;
  requiredMsg?: string;
  component: {
    name: string;
    enum?: {
      value: string;
      label: string;
    }[];
    maxLength?: number;
    placeholder?: string;
  };
  shown?: string;
}[];

export type AssistantsOutputSchema = {
  label: string;
  value?: string;
};
export type AssistantsSchema = {
  value: string;
  label: string;
  input: AssistantsInputSchema;
  output: AssistantsOutputSchema;
}[];
export type AssistantsAIGeneratedData = {
  content: string;
}


export type BadgeType = 'none' | 'pure' | 'normal' | 'primary' | 'hyper';
export type LadderPrice = {
  price: number;
  coins: number;
  label: string;
  desc: string;
  badge: BadgeType;
  // vip 开通
  vip: {
    coins: number;
    label: string;
    desc: string;
    badge: BadgeType;
    vipDesc: string;
  },
  // 场景
  stage: ('pricing' | 'wx' | 'pc')[];
  users: ('normal' | 'plus' | 'plus_3x' | 'plus3x')[];
}

export type PaymentRightsItem = {
  label: string;
  tip?: string;
  badge?: string;
  icon?: keyof typeof Icons | '';
  linkto?: string;
  isDesc?: boolean;
  isNew?: boolean;
}

export type LadderVipPayment = {
  label: string;
  days: number;
  price: number;
  priceDiscountedPerMonth: number;
  priceOriginalPerMonth: number;
  desc: string;
  badge: BadgeType;
  rightsList: PaymentRightsItem[];
  users?: ('normal' | 'plus' | 'plus_3x' | 'plus3x')[];
}

export type UpgradeVipFeature = {
  label: string;
  title: string;
  desc: string;
  button: {
    type: string;
    label: string;
  },
  vipTabas: boolean;
  rightsList: PaymentRightsItem[];
}

export type PaymentUpgradeSet = {
  title: string;
  plus3xSubtitle?: string;
  features: UpgradeVipFeature[];
  items: LadderVipPayment[];
}

export type BenefitsCompareColumnKey = 'basic' | 'plus' | 'plus3x';

export type BenefitsCompareRow = {
  feature: string;
  basic: string;
  plus: string;
  plus3x: string;
  tips?: Partial<Record<BenefitsCompareColumnKey, string>>;
  basicTip?: string;
  plusTip?: string;
  plus3xTip?: string;
  tipIconTone?: 'accent' | 'default';
};

export type BenefitsCompareConfig = {
  learnMoreUrl?: string;
  sectionBreakRowIndex?: number;
  headers?: Partial<Record<'feature' | BenefitsCompareColumnKey, string>>;
  /** 三个支付 tab 共用同一份权益数据，按 tab 配置列顺序。 */
  columnOrders?: Partial<Record<'memberPlus' | 'memberPlus3x' | 'recharge', BenefitsCompareColumnKey[]>>;
  rows: BenefitsCompareRow[];
};

export type FollowUsItem = {
  qrCode: string;
  account: string;
  label: string;
  icon: string;
  url?: string;
}

export type DidYouKnowConfigItem = {
  title: string;
  content: string;
  tag: string[];
}

export type DidYouKnowConfig = {
  title: string;
  items: DidYouKnowConfigItem[];
}

export type CommonProblem = {
  title: string;
  content: string;
  tag: string[];
}

export type SystemAnnouncementItem = {
  // 公告内容；配置 linkto 时按纯文本展示，避免与整条跳转链接嵌套 <a>
  content: string;
  // 点击整条公告跳转（站内路径或 https 外链）；后端 link_to 会转为 linkTo
  linkto?: string;
  linkTo?: string;
  // 公告的页面路径，用于控制展示公告的页面
  // wx:home; wx:summary; pc:default; pc:papers; ...
  pages: string[];
};

export type SystemAnnouncement = SystemAnnouncementItem[];

function buildAnnouncementPageKey(pathname: string): string {
  const regex = /^pages\/([^/]+(?:\/[^/]+)*)\/index(?:\?[^#]*)?(?:#.*)?$/;
  const match = pathname.match(regex);
  if (match?.[1]) {
    return `wx:${match[1]}`;
  }
  return `pc:${pathname.replace(/^\//, '') || 'default'}`;
}

function matchAnnouncementPage(pageKey: string, page: string): boolean {
  const normalized = page.trim();
  if (!normalized) {
    return false;
  }
  return pageKey === normalized || pageKey.startsWith(`${normalized}/`);
}

function resolveAnnouncementItem(
  items: SystemAnnouncementItem[],
  pageKey: string,
): SystemAnnouncementItem | null {
  let matched: SystemAnnouncementItem | null = null;
  items.forEach((item) => {
    const pages = Array.isArray(item.pages) ? item.pages : [];
    const found = pages.some((page) => matchAnnouncementPage(pageKey, String(page)));
    if (found) {
      matched = item;
    }
  });
  return matched;
}

export type SystemMaintenance = {
  content: string;
  title: string;
  maintenance: boolean;
  // wx:home; wx:summary; wx:workfllow; pc:home; pc:papers; ...
  whitelist: string[];
};

export type FeedbackKeys = 'score' | 'suggestions' | 'content' | 'contact' | 'summaryScore';
export type FeedbackScene = 'default' | 'home' | 'assistants' | 'summary' | 'summaryFigure' | 'translation' | 'aiCol' | 'aiColCell' | 'aiColBasicCell' | 'paperSearch' | 'research-drawing' | 'outlineAgent' | 'desktopIssue';
export type Feedback = {
  title: Record<FeedbackScene, string>;
  scene: Record<FeedbackScene, string[]>;
  score: {
    key: "score";
    label: string;
    required: boolean;
    options: {
      emoji: string;
      label: string;
      score: number;
      maxSelection: number;
      suggestions: string[];
      style: {
        borderColor: string;
        backgroundColor: string;
        color: string;
      }
    }[]
  };
  summaryScore: {
    key: "score";
    label: string;
    required: boolean;
    options: {
      emoji: string;
      label: string;
      score: number;
      maxSelection: number;
      suggestions: string[];
      style: {
        borderColor: string;
        backgroundColor: string;
        color: string;
      }
    }[]
  };
  suggestions: {
    key: "suggestions";
    required: boolean;
    label: string;
  };
  content: {
    key: "content";
    required: boolean;
    maxLength: number;
    placeholder: string;
  };
  contact: {
    key: "contact";
    required: boolean;
    label: string;
    tip: string;
  };
} & {
  [key: string]: {
    key: string;
    [key: string]: any;
  }
};

export type AIColsConfig = {
  // 最大可定义数量
  customLimit: number;
  // vip 最大可定义数量
  customVipLimit: number;
  // 最大可开启的列数量
  aiColsLimit: number;
  // vip最大可开启的列数量
  aiColsVipLimit: number;
  // 预设AI列价格
  presetPrice: number;
  // 自定义AI列价格
  customPrice: number;
  // 智能解析价格
  chartPrice: number;
  // 用户信
  letter: {
    width: number;
    height: number;
    url: string;
    tip: string;
  };
}

export type EventLogType = {
  platform?: 'web' | 'cx';
  element: string;
  data?: Record<string, any>;
  event?: 'click';
  path?: string;
}
interface CommonFetchable<T> {
  value: T | null;
  loading: boolean;
  fetch: () => Promise<void>;
}

type ChapterItemRange = {
  range: [number, number];
}

type ChapterItemUnit = {
  unit: '字' | '词' | '';
}

type ChapterItemRangeUnit = ChapterItemRange & ChapterItemUnit;

export type DefaultChapterItem = {
  title: string;
  subTitle: string;
  level: number;
} & ChapterItemRange;
export type ChapterTocConfig = {
  defaultChapters: Record<string, Record<string, {
    header: DefaultChapterItem[];
    footer: DefaultChapterItem[];
  }>>;
  generalChapters: Record<string, Record<string, ChapterItemRangeUnit>>
};

export type PaperSearchDomainConfig = {
  key: string;
  label: string;
  contents: (string | SearchHistoryItem)[];
};

export type PaperSearchFilterConfig = {
  yearPresets: Array<{
    label: string;
    years?: number;
    custom?: boolean;
  }>;
  types: Array<{
    key: string;
    label: string;
  }>;
  yearRange: {
    min: number;
    max: "current" | number;
  };
};

export type PaperSearchConfig = {
  filters: PaperSearchFilterConfig;
  domains: PaperSearchDomainConfig[];
  paperTypes: Array<{
    key: string;
    label: string;
  }>;
};

export type DesktopDownloadOption = {
  id: string;
  platform: 'mac' | 'windows';
  sourceId: string;
  sourceLabel: string;
  label: string;
  shortLabel: string;
  detail: string;
  fileType: string;
  url?: string;
  available: boolean;
  previewImage?: DesktopDownloadHeroImage;
};

export type DesktopDownloadHeroImage = {
  src: string;
  width: number;
  height: number;
  alt?: string;
};

export type DesktopDownloadsConfig = {
  version?: string;
  gitCommit?: string;
  heroImage?: DesktopDownloadHeroImage;
  downloads: DesktopDownloadOption[];
};

/** `POST .../common/config` 请求体中的 `key`：Plus 3倍会员档位（与 `payment_upgrade_set` 同结构） */
export const COMMON_CONFIG_KEY_PAYMENT_UPGRADE_SET_PLUS_3X = 'payment_upgrade_set_plus_3x';

export type CitationStyleItem = {
  display: string;
  style: string;
};

export type CitationStylesConfig = Record<string, CitationStyleItem>;

export type AgentPromptConfigItem = {
  id: string;
  title?: string;
  label?: string;
  prompt: string;
  icon?: string;
  quick?: boolean;
  isQuick?: boolean;
};

export type AgentPromptConfigGroup = {
  id: string;
  title?: string;
  label?: string;
  items: AgentPromptConfigItem[];
};

export type AgentPromptConfig = {
  groups: AgentPromptConfigGroup[];
};

export type OutlineAgentPromptConfigItem = AgentPromptConfigItem;
export type OutlineAgentPromptConfigGroup = AgentPromptConfigGroup;
export type OutlineAgentPromptConfig = AgentPromptConfig;
export type WriterAgentPromptConfig = AgentPromptConfig;

function initialCommonFetchable<T>(
  key: keyof CommonFetchSlice,
  name: string,
  get: () => CommonSlice,
) {
  return {
    value: null,
    loading: false,
    fetch: () => get().commonFetch(key, name),
  };
}

export type CommonFetchSlice = {
  // 获取梯度价格
  ladderPrices: CommonFetchable<LadderPrice[]>;
  // 获取梯度价格
  paymentUpgradeSet: CommonFetchable<PaymentUpgradeSet>;
  /** Plus 3倍会员套餐：与 payment_upgrade_set 同结构 */
  paymentUpgradeSetPlus3x: CommonFetchable<PaymentUpgradeSet>;
  /** 支付弹窗权益对比配置：三个 tab 共用 rows，仅列顺序不同 */
  benefitsCompare: CommonFetchable<BenefitsCompareConfig>;
  // 获取常见问题
  commonProblems: CommonFetchable<CommonProblem[]>;
  // 获取全局配置
  globalConfig: CommonFetchable<GlobalConfig>;
  // 获取关注我们列表
  followUsList: CommonFetchable<FollowUsItem[]>;
  // 获取短链接映射
  shortUrlMap: CommonFetchable<Record<string, string>>;
  // 获取你知道吗配置
  didYouKnowConfig: CommonFetchable<DidYouKnowConfig>;
  // SystemAnnouncement 系统公告
  systemAnnouncement: CommonFetchable<SystemAnnouncement> & {
    content: string;
    linkto: string;
    getContent: (path: string) => Promise<string>;
  };
  systemMaintenance: CommonFetchable<SystemMaintenance>;
  feedback: CommonFetchable<Feedback>;
  aiColsConfig: CommonFetchable<AIColsConfig>;
  chapterTocConfig: CommonFetchable<ChapterTocConfig>;
  // 获取论文搜索配置
  paperSearchConfig: CommonFetchable<PaperSearchConfig>;
  // 获取桌面端下载页配置
  desktopDownloads: CommonFetchable<DesktopDownloadsConfig>;
  // 获取引用格式配置
  citationStylesConfig: CommonFetchable<CitationStylesConfig>;
  // 获取大纲 Agent 常用指令配置
  outlineAgentPromptConfig: CommonFetchable<OutlineAgentPromptConfig>;
  // 获取 Writer Agent 常用指令配置
  writerAgentPromptConfig: CommonFetchable<WriterAgentPromptConfig>;
};

export type CommonSlice = {
  assistants: AssistantsSchema | null;
  assistantsOutput: Record<string, AssistantsOutputSchema['value']>;
  updateAssistantsOutput: (tab: string, value: string) => void;
  assistantsAILoading: boolean;
  initialAssistants: () => Promise<void>;
  assistantsAIGenerated: (tab: string, assistantsInput: Record<string, string>) => Promise<void>;
  commonFetch: <T>(
    key: keyof CommonSlice,
    name: string,
  ) => Promise<void>;
  eventLog: (log: EventLogType) => Promise<void>;
} & CommonFetchSlice;


export const createCommonSlice: StateSlice<CommonSlice> = (set, get) => ({
  assistants: null,
  assistantsOutput: {},
  updateAssistantsOutput: (key, value) => {
    set(state => {
      state.assistantsOutput[key] = value;
    });
  },
  assistantsAILoading: false,
  initialAssistants: async () => {
    if (get().assistants) {
      return;
    }
    const responseData = await queryCommonConfig<AssistantsSchema>('learn_helper');
    if (responseData?.code === 0) {
      set(state => {
        state.assistants = snakeToCamel(responseData.data);
      });
    }
  },
  assistantsAIGenerated: async (tab, input) => {
    if (get().assistantsAILoading || !tab) {
      return;
    }
    set(state => {
      state.assistantsAILoading = true;
    });
    try {
      const { prompt_key, ...params } = input;
      const formData = new FormData();
      formData.append('prompt_key', prompt_key);
      Object.keys(params).forEach((key) => {
        formData.append(`params[${key}]`, params[key]);
      });
      const { code, data, message: msg } = (await fun.post<WrapResponse<AssistantsAIGeneratedData>>(
        api('/helper/complete', true),
        formData,
      )).data;
      if (code === 0) {
        const res = snakeToCamel<AssistantsAIGeneratedData>(data);
        get().updateAssistantsOutput(tab, res.content);
        get().refreshUserInfo();
      } else if (code === 5001) {
        get().setBalanceInsufficient(true);
      } else {
        toast.error(msg);
      }
    } catch (error) {
      console.error('Error query common/config of learn_helper:', error);
    } finally {
      set(state => {
        state.assistantsAILoading = false;
      });
    }
  },
  commonFetch: async <T extends Object>(
    key: keyof CommonSlice,
    name: string,
  ): Promise<void> => {
    const state = get();

    const commonState = state[key] as CommonFetchable<T>;

    // 检查是否已经有值或正在加载
    if (commonState.value || commonState.loading) {
      return;
    }

    // 开始加载
    set((state) => {
      (state[key] as CommonFetchable<T>).loading = true;
    });

    try {
      // 执行查询操作
      const responseData = await queryCommonConfig<T>(name);
      if (responseData?.code === 0) {
        // 设置查询结果
        set((state) => {
          (state[key] as CommonFetchable<T>).value = snakeToCamel<T>(responseData.data);
        });
      }
    } catch (error) {
      console.error(`Error fetching data for key ${key}:`, error);
      // 这里可以处理错误，例如设置一个错误状态
    } finally {
      // 结束加载
      set((state) => {
        (state[key] as CommonFetchable<T>).loading = false;
      });
    }
  },

  ladderPrices: {
    ...initialCommonFetchable<LadderPrice[]>('ladderPrices', 'ladder_prices_v2', get),
  },
  paymentUpgradeSet: {
    ...initialCommonFetchable<PaymentUpgradeSet>('paymentUpgradeSet', 'payment_upgrade_set', get),
  },
  paymentUpgradeSetPlus3x: {
    ...initialCommonFetchable<PaymentUpgradeSet>(
      'paymentUpgradeSetPlus3x',
      COMMON_CONFIG_KEY_PAYMENT_UPGRADE_SET_PLUS_3X,
      get,
    ),
  },
  benefitsCompare: {
    ...initialCommonFetchable<BenefitsCompareConfig>(
      'benefitsCompare',
      'benefits_compare',
      get,
    ),
  },
  commonProblems: {
    ...initialCommonFetchable<CommonProblem[]>('commonProblems', 'common_problems', get),
  },
  globalConfig: {
    ...initialCommonFetchable<GlobalConfig>('globalConfig', 'global_config', get),
  },
  shortUrlMap: {
    ...initialCommonFetchable<Record<string, string>>('shortUrlMap', 'short_map', get),
  },
  followUsList: {
    ...initialCommonFetchable<FollowUsItem[]>('followUsList', 'follow_us_list', get),
  },
  didYouKnowConfig: {
    ...initialCommonFetchable<DidYouKnowConfig>('didYouKnowConfig', 'did_you_know', get),
  },
  systemAnnouncement: {
    ...initialCommonFetchable<SystemAnnouncement>('systemAnnouncement', 'system_announcement', get),
    content: '',
    linkto: '',
    async getContent(pathname) {
      await get().systemAnnouncement.fetch();
      const pageKey = buildAnnouncementPageKey(pathname);
      const items = get().systemAnnouncement.value || [];
      const matched = resolveAnnouncementItem(items, pageKey);
      const content = matched?.content?.trim() || '';
      const linktoRaw = matched?.linkto ?? matched?.linkTo;
      const linkto = typeof linktoRaw === 'string' ? linktoRaw.trim() : '';
      const current = get().systemAnnouncement;
      if (current.content === content && current.linkto === linkto) {
        return content;
      }
      set((state) => {
        state.systemAnnouncement.content = content;
        state.systemAnnouncement.linkto = linkto;
      });
      return content;
    },
  },
  systemMaintenance: {
    ...initialCommonFetchable<SystemMaintenance>('systemMaintenance', 'system_maintenance', get),
  },
  feedback: {
    ...initialCommonFetchable<Feedback>('feedback', 'feedback', get),
  },
  aiColsConfig: {
    ...initialCommonFetchable<AIColsConfig>('aiColsConfig', 'ai_cols_config', get),
  },
  chapterTocConfig: {
    ...initialCommonFetchable<ChapterTocConfig>('chapterTocConfig', 'chapter_toc_config', get),
  },
  paperSearchConfig: {
    ...initialCommonFetchable<PaperSearchConfig>('paperSearchConfig', 'paper_search_config', get),
  },
  desktopDownloads: {
    ...initialCommonFetchable<DesktopDownloadsConfig>('desktopDownloads', 'desktop_downloads', get),
  },
  citationStylesConfig: {
    ...initialCommonFetchable<CitationStylesConfig>('citationStylesConfig', 'citation_styles', get),
  },
  outlineAgentPromptConfig: {
    ...initialCommonFetchable<OutlineAgentPromptConfig>(
      'outlineAgentPromptConfig',
      'outline_agent_prompt_config',
      get,
    ),
  },
  writerAgentPromptConfig: {
    ...initialCommonFetchable<WriterAgentPromptConfig>(
      'writerAgentPromptConfig',
      'writer_agent_prompt_config',
      get,
    ),
  },
  eventLog: async (log: EventLogType) => {
    try {
      const { data, code } = (await fun.post(
        api('/common/eventLog', true), {
        platform: log.platform ?? 'web',
        element: log.element,
        data: log.data,
        event: log.event ?? 'click',
        path: log.path ?? window.location.pathname,
      })).data;
      if (code === 0) {

      }
    } catch (error) {
      console.error('Error post eventLog:', error);
    } finally {

    }
  },
});

export async function queryCommonConfig<T extends Object>(key: string) {
  try {
    return (await fun.post<WrapResponse<T>>(
      api('/common/config', true),
      {
        key,
      },
    )).data;
  } catch (error) {
    console.error(`Error query common/config by ${key}`, error);
    return null;
  }
}
